'use client';

import { useRef } from 'react';
import { useRouter } from 'next/navigation';
import { LiveSearchBox, t } from '@akademi/site-builder-runtime';
import type { SearchSectionConfig, ShellConfig } from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { ChromeStyles } from './ChromeStyles';
import type { SolarChromeProps } from './types';

/**
 * Whether the operator wants a header search at all, and where its results go.
 *
 * <p>Both `search.showSearch` and the older `header.showSearch` are read,
 * because the Shell editor has written the flag in both places over time and a
 * shop configured through the older one would otherwise lose its search bar.
 * Unset means shown — that is the runtime's own default, and changing it here
 * would silently blank the box on a shop that never touched the setting.
 */
export function headerSearchFor(config: ShellConfig, locale: string, fallback: string) {
  const search: SearchSectionConfig = config.search ?? {};
  const shown = search.showSearch ?? config.header?.showSearch ?? true;
  if (!shown) return null;
  return {
    // The operator authors the placeholder under `search`, which is where the
    // type documents it; the runtime reads `header.searchPlaceholder` instead
    // and so ignores it. Both are read here, `search` first.
    placeholder:
      t(search.searchPlaceholder ?? config.header?.searchPlaceholder, locale, fallback) ||
      undefined,
    resultsHref: search.resultsHref ?? '/search',
  };
}

/**
 * The header search bar: one wide box, attached to its own Search button, with
 * suggestions dropping down under it.
 *
 * <h3>Why this exists rather than the shell's own search</h3>
 *
 * <p>The shell's `search` section already carries `width: 'xwide'` and
 * `searchInline: true` — the operator asked for a wide, non-modal box. The
 * runtime discards both. `resolveCatalogSearch` returns `undefined` for
 * `mode: 'suggest'`, which is the mode a commerce storefront MUST use (its
 * products are not courses), and the header then reads `width` and
 * `searchInline` off that same `undefined`:
 *
 * <pre>
 *   width={SEARCH_WIDTH_PRESETS_PX[catalogSearch?.width ?? 'default']}  // 220px
 *   expandToDialog={!catalogSearch?.searchInline}                       // a modal
 * </pre>
 *
 * <p>So a suggest-mode host gets a 220px pill that opens a full-screen dialog
 * and cannot configure its way out: both settings are silently dropped. The
 * fix belongs in the base — the two reads should come from `config.search`,
 * not from the catalog-only resolution of it — but the base is pinned here and
 * a shop is trading against it today.
 *
 * <p>`headerSearchSlot` is the base's own documented seam for exactly this
 * ("Optional override for the header's search affordance"), so nothing is
 * overridden by force: the header renders this INSTEAD of its pill, and
 * `showDefault` already turns the built-in one off when an override is passed.
 * <strong>This is not a header component.</strong> The shell still owns the
 * row, the brand, the menu and the actions, and every string here still comes
 * from the operator's own `search` section.
 *
 * <p>It belongs to THIS VERTICAL, not to the storefront. One storefront process
 * serves every shop through one `SiteShell`, so a bar written there reaches all
 * of them and is held off the rest by whatever their config happens to say —
 * a shop's appearance resting on a value nobody guards. Here it is selected by
 * the site's own `enabledPlugins`, so a kitchen shop cannot inherit it.
 *
 * <h3>The button reads the input through the DOM</h3>
 *
 * <p>`LiveSearchBox` owns the query state and exposes no imperative handle, so
 * the Search button next to it cannot be told what to submit. It reads the
 * value off the input inside its own ref'd subtree instead. That is one
 * `querySelector` over markup this component rendered, and it navigates to the
 * same place Enter does — `resultsHref?q=` — so the two affordances cannot
 * drift apart. The alternative is reimplementing the suggestion dropdown,
 * debouncing, recents and the combobox keyboard contract, which would drift
 * from the base on the first update.
 */
export function SolarHeaderSearch({ config, locale, localeFallback }: SolarChromeProps) {
  const router = useRouter();
  const label = useSolarText();
  const root = useRef<HTMLDivElement | null>(null);
  const search = headerSearchFor(config, locale, localeFallback);
  const resultsHref = search?.resultsHref ?? '/search';

  function submit() {
    const input = root.current?.querySelector<HTMLInputElement>('input[role="combobox"]');
    const query = input?.value.trim();
    // An empty box is not a search. Focusing it says so without a message.
    if (!query) {
      input?.focus();
      return;
    }
    router.push(`${resultsHref}?q=${encodeURIComponent(query)}`);
  }

  // The operator switched the search off. Nothing renders, and the shell falls
  // back to its own affordance — which is also off, because `showSearch` is
  // what it reads too.
  if (!search) return null;

  return (
    <div ref={root} className="ae ae-search">
      <ChromeStyles />
      <LiveSearchBox
        tenantSlug=""
        variant="inline"
        // The whole point: results drop down under the box, in place, rather
        // than throwing a full-screen dialog over the page.
        expandToDialog={false}
        fillWidth
        placeholder={search.placeholder}
        resultsHref={resultsHref}
        locale={locale}
        localeFallback={localeFallback}
      />
      <button
        type="button"
        className="ae-search__go"
        // The button is a magnifier, so the accessible name is the only name
        // it has.
        aria-label={label('chrome.search')}
        // mousedown would blur the input first and close the dropdown under
        // the cursor, so the click lands on nothing.
        onMouseDown={(event) => event.preventDefault()}
        onClick={submit}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.6-3.6" />
        </svg>
      </button>
    </div>
  );
}
